/**
 * Pokémon GO Pokédex variant classifier (Push 5, v1.4.2 b1).
 *
 * The Pokédex pivot replaces the broad "is this a PoGo screen?" router with
 * a dedicated identifier for the Pokédex screen and its active "Dex type"
 * (the bottom carousel selector text: POKÉMON, SHINY, LUCKY, XXL, XXS,
 * G-MAX, MEGA, SHADOW, PURIFIED, 100%).
 *
 * Why a new module instead of editing pokedexOcr.ts?
 *   - pokedexOcr.ts targets the old per-region progress-bar workflow
 *     ("Kanto Caught 142/151"). It still works for the legacy aggregate
 *     /api/dex/pokedex-scan endpoint and we keep it behind the pivot for
 *     scan-history backfill.
 *   - The new flow needs cell-level state (dex number + present/missing)
 *     keyed by Dex *variant*, not region/filter. The bottom selector text
 *     is the primary signal (per user direction); header icon/colour/count
 *     are supporting only.
 *   - We must NEVER create inventory/instance records from these crops.
 *     This module exposes only Dex-state-shaped types.
 *
 * Sparse tabs:
 *   Shadow / Purified / Mega / G-Max often skip numbers and don't start
 *   at 001. We never imply "missing" for numbers we did not actually see
 *   — only the visible cell numbers in the frame are updated.
 *
 * Overlay fencing:
 *   The Regions floating button (bottom-right) and search/X/filter
 *   controls can occlude the lowest rows. The caller passes a frame's
 *   width/height; we exclude the bottom ~14% of cells from being treated
 *   as authoritative "missing" markers (still ingested as present if a
 *   sprite is visible) so we never persist garbage from overlay regions.
 */

// ── Types ────────────────────────────────────────────────────────────────

/**
 * Canonical Dex variant identifiers, used as keys in the dexStateStore.
 *
 * Naming notes:
 *   - "pokemon" is the catch-all main Pokédex tab (in-game text: POKÉMON).
 *   - "perfect" maps to "100%" / 3-star in-game text.
 *   - "gmax" maps to G-MAX / GIGANTAMAX in-game text.
 *
 * The in-game order in the bottom carousel today is roughly:
 *   POKÉMON · SHINY · LUCKY · XXL · XXS · MEGA · G-MAX · SHADOW · PURIFIED · 100%
 * but we treat the ordering as cosmetic — the active variant is whichever
 * label sits inside the center pill.
 */
export type PokedexVariant =
  | "pokemon"
  | "shiny"
  | "lucky"
  | "perfect"
  | "xxs"
  | "xxl"
  | "mega"
  | "gmax"
  | "shadow"
  | "purified"
  | "complete" // pokedex-complete-dex: DERIVED, read-only rollup (no scan / no manual)
  | "shundo" // b18-shundo-dex: manual-only, no scan path
  | "shinyLucky" // b19-shiny-lucky-dex: manual-only, no scan path
  | "dynamax" // dmax-dynamax-dex: manual-only + curated roster, no scan path
  // form-dexes: manual-only + curated roster, and their cells are FORMS rather
  // than species (see FORM_DEX_VARIANTS below).
  | "regional"
  | "costume";

/**
 * Variants that have no scan path and are excluded from ALL_VARIANTS,
 * DEX_CATEGORY_ORDER, and the OCR classifier. They live in their own
 * "extras" group to the right of the main tab strip.
 * b18-shundo-dex: shundo is the first manual-only variant.
 * dmax-dynamax-dex: dynamax is manual-only too (Dynamax is not a scannable
 * registered Pokédex screen in Pokémon GO) but, unlike shundo/shinyLucky, it
 * carries a curated roster (DYNAMAX_DEX_IDS) so its grid shows silhouettes.
 */
export const MANUAL_ONLY_VARIANTS: readonly PokedexVariant[] = [
  "shundo", "shinyLucky", "dynamax", "regional", "costume",
];

/**
 * Dexes whose CELLS ARE FORMS, not species: the Regional dex (every Alolan /
 * Galarian / Hisuian / Paldean form) and the Costume dex (every GO event
 * costume). A species appears once per form it has, so Meowth is two cells on
 * Regional and Bulbasaur four on Costume.
 *
 * They are manual-only like the custom dexes, and additionally excluded from the
 * per-species detail tile grid (DETAIL_VARIANT_ORDER): one tile cannot stand for
 * four costumes, and the species page already lists a species' forms and
 * costumes in their own sections. The roster and per-cell data live in
 * lib/formDexRoster.ts; this constant is here so the type home stays the single
 * place that says which variants exist and how each behaves.
 */
export const FORM_DEX_VARIANTS: readonly PokedexVariant[] = ["regional", "costume"];

/**
 * pokedex-complete-dex: DERIVED variants have no scan path AND no manual-edit
 * path — their per-species state is COMPUTED from other dexes, never stored or
 * user-toggled. Like MANUAL_ONLY_VARIANTS they are excluded from ALL_VARIANTS,
 * DEX_CATEGORY_ORDER, and the OCR classifier; unlike them they are also
 * excluded from every write path (setManual/scan). "complete" lights a species
 * up once it is caught across every dex that applies to it.
 */
export const DERIVED_VARIANTS: readonly PokedexVariant[] = ["complete"];

/** True for variants whose state is computed, never scanned or manually set. */
export function isDerivedVariant(v: PokedexVariant): boolean {
  return DERIVED_VARIANTS.includes(v);
}

export const ALL_VARIANTS: PokedexVariant[] = [
  "pokemon", "shiny", "lucky", "perfect", "xxs", "xxl",
  "mega", "gmax", "shadow", "purified",
];

/**
 * Sparse variants do NOT contain every species in their region; they show
 * only currently-available entries. The scanner must never infer missing
 * numbers for these tabs — only update the numbers actually visible in
 * the frame.
 */
export const SPARSE_VARIANTS: ReadonlyArray<PokedexVariant> = [
  "shadow", "purified", "mega", "gmax",
];

/**
 * Canonical left→right order of the Dex category tabs, matching the Pokémon
 * GO bottom carousel sequence the user wants Nexus Dex to mirror (Push 2 /
 * v1.4.4 b3). This is the SINGLE source of truth for tab order and for the
 * order pages swipe through — the Pokédex page and any swipe container read
 * from here so they can never drift apart.
 *
 * Order (Nexus Dex canonical — b18a item 2):
 *   All · Shiny · Lucky · XXL · XXS · Perfect (100%) · G-Max · Mega · Shadow · Purified
 *
 * Deliberate divergence from PoGo: in PoGo the "Perfect" (100%) pill sits
 * LAST, after Purified. Nexus keeps Perfect right after XXS instead. Rationale:
 * every Pokémon is available in every dex type up to and including Perfect, so
 * [All, Shiny, Lucky, XXL, XXS, Perfect] are the full-coverage types; from
 * G-Max onward the dex types are partial-coverage (only some Pokémon qualify):
 * [G-Max, Mega, Shadow, Purified]. Grouping the full-coverage types first, then
 * the partial-coverage ones, is the natural reading order for Nexus.
 *
 * This is the SINGLE source of truth: the Pokédex pill row and the horizontal
 * swipe nav both read from here, so they can never drift apart. Adjust here and
 * every consumer follows. (The OCR on-screen pill contract in scanSignals.ts —
 * PILL_ORDER — is a SEPARATE constant that mirrors the real in-game layout for
 * Tier-2 order inference and must NOT be reordered to match this.)
 */
export const DEX_CATEGORY_ORDER: readonly PokedexVariant[] = [
  "pokemon",
  "shiny",
  "lucky",
  "xxl",
  "xxs",
  "perfect",
  "gmax",
  "mega",
  "shadow",
  "purified",
] as const;

/**
 * The category reached by swiping `dir` (+1 = swipe left / next, -1 = swipe
 * right / previous) from `current`, following [[DEX_CATEGORY_ORDER]]. Like
 * the Pokémon GO bottom carousel, navigation is CLAMPED at the ends (it does
 * not wrap), so swiping past Purified or before Pokémon stays put. Returns
 * `current` unchanged when the variant is not in the order (defensive).
 */
export function adjacentCategory(
  current: PokedexVariant,
  dir: 1 | -1,
): PokedexVariant {
  const idx = DEX_CATEGORY_ORDER.indexOf(current);
  if (idx === -1) return current;
  const next = idx + dir;
  if (next < 0 || next >= DEX_CATEGORY_ORDER.length) return current;
  return DEX_CATEGORY_ORDER[next];
}

export type PokedexRegionLite =
  | "kanto" | "johto" | "hoenn" | "sinnoh" | "unova"
  | "kalos" | "alola" | "galar" | "hisui" | "paldea"
  // GO's region-less grouping for Meltan #808 / Melmetal #809.
  | "unidentified" | "unknown";

export type CellState = "present" | "missing" | "unknown";

export interface VariantCellObservation {
  dexNumber: number;
  state: CellState;
  /** Approximate cell coordinates (0-1) for dedupe + overlay fencing. */
  yPct?: number;
  /**
   * 1-based grid slot index this observation came from, when the live adapter
   * produced it. Carried so the write-chokepoint veto logs (invalid dex member,
   * two-frame confirmation) can name the exact cell. Optional — the upload path
   * and older callers omit it.
   */
  slotIndex?: number;
  /**
   * Sprite/foreground match confidence 0..1 for this cell, when the live adapter
   * produced it. Carried so the session's tiered confirmation (Root Cause D) can
   * fast-path a single very-high-confidence "caught" cell, and so the OCR sanity
   * pass can detect appended-digit ladders by near-identical sprite confidence.
   * Optional — the upload path and older callers omit it.
   */
  spriteConfidence?: number;
  /**
   * Sprite verdict for this cell ("caught" / "silhouette" / "empty" / review
   * band), when available. Used alongside `spriteConfidence` by the tiered
   * confirmation fast-path. Optional.
   */
  spriteVerdict?: string;
  /**
   * Raw sprite cell-state for this observation ("caught" / "silhouette" /
   * "empty" / "none"), when available. Distinct from `spriteVerdict`: a
   * silhouette cell rescued by a chroma/presence heuristic can still carry
   * `spriteVerdict === "caught"`, so only `spriteState === "caught"` positively
   * identifies a real caught body. Round-15 uses it as the discriminator for the
   * caught-state single-frame apply tier (it excludes the empty/silhouette cells
   * that can otherwise read a high histSim). Optional — omitted by callers that
   * do not carry per-cell sprite state.
   */
  spriteState?: string;
  /** Where the dex number sat in the card ("center" = blank-card layout). */
  numberPosition?: string;
  /** Caught/blank judged from the card band above the Vision number box. */
  numberAnchored?: boolean;
  /**
   * Sprite match provenance for this observation (e.g. "chroma-match",
   * "aura-present", "local-presence"), when available. Threaded through from
   * the frame adapter so the session's instant-vision apply path can refuse to
   * auto-apply on the fragile chroma-only "local-presence" fallback (which
   * false-applied #024 on the 2026-07-06 device scan). Optional — omitted by
   * the upload path and callers that do not carry sprite provenance.
   */
  spriteReason?: string;
  /**
   * Fraction (0..1) of THIS cell's ROI covered by the Nexus PiP overlay, from
   * the classifier's `computePillOverlapFraction`. Threaded through so the
   * session's PiP-overlap guard (scan overhaul v2, Phase 4) can defer applying a
   * cell whose sprite ROI is >15% blended by the semi-transparent PiP (the
   * classifier runs on PiP-corrupted pixels there and cannot be trusted). Omitted
   * on the upload path and callers that do not carry pill geometry.
   */
  pillOverlapFraction?: number;
  /**
   * True when this cell's verdict was replayed from the per-cell content cache
   * (unchanged pixels) rather than classified from this frame. Threaded through
   * so the session's flip-flop guard only counts INDEPENDENT looks at a cell:
   * a cached read is the previous verdict repeated, so letting it corroborate
   * would turn "hold still" into "confirm whatever you first read". Omitted by
   * the upload path and callers with no cache, which read as fresh.
   */
  classificationCached?: boolean;
  /**
   * Presence-classifier chroma fraction (0..1) for THIS cell — the fraction of
   * the sprite region that is chromatic rather than monochrome. Threaded through
   * so the evidence-accumulation commit can tell a small COLOUR catch (real
   * chroma + mass, but the pixel classifier under-read it as "empty") from a
   * genuinely empty grey slot (chroma ~0.00). Empty cells read ~0.00-0.03; a real
   * coloured sprite reads >0.05. Optional — omitted by the upload path.
   */
  spriteChromaFraction?: number;
  /**
   * Presence-classifier foreground pixel MASS for THIS cell. A real sprite fills
   * the body (~2000+ px); a pure-aura phantom carries only ~500-780 px (glyph +
   * glow). Paired with `spriteChromaFraction` to separate an under-read colour
   * catch from an empty slot. Optional — omitted by the upload path.
   */
  spriteForegroundPixels?: number;
  /**
   * The SAME body measurement as `spriteForegroundPixels`, but normalised by ROI
   * area and with the dex-number box masked out (`bodyFrac`), plus the body's
   * internal luminance spread (`lumStd`). Both come from `classifyCellPresence`.
   *
   * These exist because the raw pixel count above is device-dependent — it
   * scales with tile size, so a floor calibrated on one handset drifts on
   * another — and because two separate device traces showed the count alone no
   * longer separates a real catch from a false write: 2026-07-21 (a genuine
   * catch at #334 read 726-780 px while a false write at #294 committed at
   * 1022), and the shiny 2fps pass, where chroma and mass together still could
   * not separate a dim same-hue catch from background bleed. `lumStd`/`bodyFrac`
   * are the structure signal that does. Omitted by the upload path and callers
   * with no presence classification.
   */
  spriteBodyFrac?: number;
  spriteLumStd?: number;
}

export interface VariantFrame {
  variant: PokedexVariant;
  /** Region label from header card, when readable. */
  region: PokedexRegionLite;
  /** Header progress count "138" -> 138. Null when unreadable. */
  headerCount: number | null;
  /** Header total when shown e.g. "151/151". Often missing for sparse tabs. */
  headerTotal: number | null;
  /**
   * Cells the scanner saw in this frame. State is per-cell. Numbers NOT in
   * this list are NOT inferred missing — see SPARSE_VARIANTS / dedupe.
   */
  cells: VariantCellObservation[];
  /** Variant classifier confidence 0-1 (selector text + supporting signals). */
  variantConfidence: number;
  /** True when overlay fencing fired (bottom rows partially occluded). */
  overlayFenced: boolean;
}

// ── Page-colour veto map ──────────────────────────────────────────────────

/**
 * The pre-OCR gate's dex page-colour families (a subset of dexPreOcrGate.ts's
 * `PreOcrGateClass`). Re-declared here as a structural string union rather than
 * imported so the classifier stays a leaf module (no dependency cycle with the
 * gate). The non-dex gate classes (`nondex_mixed`, `pogo_menu`, etc.) are
 * intentionally NOT part of this union — they never carry a dex page-colour and
 * the veto is a no-op for them.
 */
export type DexPageColourFamily =
  | "dex_cyan"
  | "dex_golden"
  | "dex_peach"
  | "dex_pink"
  | "dex_mint"
  | "dex_shadow_purple";

/**
 * Colour-family → allowed-variants veto map. Each dex page-colour family can
 * only host the variants listed here. Verified against the page tints in
 * dexPreOcrGate.ts:651-681 + reference screenshots:
 *   - dex_cyan   (blue,    hue ~183-212) → pokemon / xxl / xxs
 *   - dex_golden (yellow,  hue ~38-62)   → shiny / lucky
 *   - dex_peach  (salmon,  hue ~5-28)    → perfect
 *   - dex_pink   (rose,    hue ~322-8)   → gmax / mega
 *   - dex_mint   (teal,    hue ~145-190) → purified
 *   - dex_shadow_purple (lavender, ~225-265) → shadow
 *
 * Colour is VETO-ONLY (b17d anti-flip-flop intent): it can reject a resolved
 * variant that is IMPOSSIBLE for the current page family, but it NEVER positively
 * picks a variant — families are shared (cyan by {pokemon,xxl,xxs}, golden by
 * {shiny,lucky}), so chip-text still disambiguates WITHIN a family. The map only
 * vetoes ACROSS families (e.g. a `shiny` resolved on a cyan page).
 */
export const COLOUR_FAMILY_ALLOWED_VARIANTS: Readonly<
  Record<DexPageColourFamily, ReadonlyArray<PokedexVariant>>
> = {
  dex_cyan: ["pokemon", "xxl", "xxs"],
  dex_golden: ["shiny", "lucky"],
  dex_peach: ["perfect"],
  dex_pink: ["gmax", "mega"],
  dex_mint: ["purified"],
  dex_shadow_purple: ["shadow"],
};

/**
 * Whether `family` is one of the dex page-colour families that carries a veto
 * set. A plain string guard so callers can pass the gate's raw class through
 * without importing the gate's type.
 */
export function isDexPageColourFamily(
  family: string | null | undefined,
): family is DexPageColourFamily {
  return family != null && family in COLOUR_FAMILY_ALLOWED_VARIANTS;
}

/**
 * VETO predicate: true when `variant` is IMPOSSIBLE for the given page-colour
 * `family` (i.e. the family is a known dex colour family AND the variant is not
 * in its allowed set). Returns false — never veto — for unknown / non-dex
 * families, so the veto is inert whenever the gate could not classify a dex
 * colour. This is the only direction colour is allowed to influence routing.
 */
export function colourFamilyVetoesVariant(
  family: string | null | undefined,
  variant: PokedexVariant,
): boolean {
  if (!isDexPageColourFamily(family)) return false;
  return !COLOUR_FAMILY_ALLOWED_VARIANTS[family].includes(variant);
}

/**
 * Variants that page COLOUR alone may never positively select, even when the
 * colour family admits exactly one variant (2026-08-30 audit decision, after
 * the inflated-Perfect incident). A colour-resolve for these needs the chip
 * text to AGREE, not merely fail to contradict:
 *   - "perfect": a hundo assertion is the costliest false write in the app,
 *     the peach hue band overlaps pink (order-resolved) and a real Perfect
 *     page has been observed cycling dex_mint / uniform_unknown frame to
 *     frame - so an unreadable pill plus a warm wash must HOLD, never fill
 *     the Perfect dex. Mint->purified and shadow_purple->shadow keep plain
 *     colour-resolve: their washes are stable and their dexes sparse.
 */
export const COLOUR_RESOLVE_CHIP_REQUIRED: ReadonlySet<PokedexVariant> = new Set<PokedexVariant>([
  "perfect",
]);

/**
 * The single allowed variant for a colour family that admits EXACTLY ONE, else
 * null. Derived from [[COLOUR_FAMILY_ALLOWED_VARIANTS]] cardinality so it tracks
 * the map automatically — no hardcoded family/variant literals. Today that is
 * dex_peach→perfect, dex_mint→purified, dex_shadow_purple→shadow; multi-variant
 * families (dex_cyan, dex_golden, dex_pink) return null because the page tint
 * alone cannot disambiguate them. Returns null for unknown / non-dex families.
 */
export function soleVariantForColourFamily(
  family: string | null | undefined,
): PokedexVariant | null {
  if (!isDexPageColourFamily(family)) return null;
  const allowed = COLOUR_FAMILY_ALLOWED_VARIANTS[family];
  return allowed.length === 1 ? allowed[0] : null;
}

/**
 * Tint-boundary adjacency map (device 2026-06-24, Purified-on-cyan deadlock).
 *
 * Some single-variant curated dex pages wash into a NEIGHBOURING family's hue
 * band on device, so the pre-OCR colour tracker misreads them as a multi-variant
 * neighbour and the variant deadlocks at mode:unknown forever. The verified case:
 * the Purified page's mint wash reads B-dominant and classifies as `dex_cyan`
 * rather than `dex_mint` (the P6 cyan/mint boundary deliberately keeps a
 * B-dominant wash as cyan — see pokedexOcr.recoverSoleVariantFromBandText). On
 * the Round-3 device log this produced 134 `dex_cyan` locks with `variant:null`
 * and ZERO purified resolutions.
 *
 * Keyed by the OBSERVED (possibly misread) family → the adjacent single-variant
 * families whose sole variant its wash can mask. Used ONLY by
 * [[boundaryRecoveredVariant]], which additionally requires app_state to NAME the
 * adjacent variant, so this is never a blind colour→variant assumption.
 */
export const COLOUR_FAMILY_TINT_BOUNDARY: Readonly<
  Record<string, ReadonlyArray<DexPageColourFamily>>
> = {
  dex_cyan: ["dex_mint"],
};

/**
 * Recover the sole variant of a tint-adjacent family when the OBSERVED page
 * colour vetoes `appStateVariant` but that variant IS the sole variant of an
 * adjacent family (per [[COLOUR_FAMILY_TINT_BOUNDARY]]). Returns the recovered
 * variant (e.g. "purified" on a `dex_cyan` page whose app_state is purified),
 * else null.
 *
 * EVIDENCE GATE: this only fires when app_state already names the variant — i.e.
 * the user actively selected that filter in PoGo, which only happens while
 * viewing that dex. A genuine cyan main/XXL/XXS page is NOT vetoed (its variant
 * is allowed on cyan) so it never reaches here, and a stale app_state naming a
 * NON-sole-variant (e.g. shiny) returns null → the caller HOLDs. So it can never
 * positively mis-map a colour to a variant on its own.
 */
export function boundaryRecoveredVariant(
  pageColourFamily: string | null | undefined,
  appStateVariant: PokedexVariant,
): PokedexVariant | null {
  if (!isDexPageColourFamily(pageColourFamily)) return null;
  // Must be a true cross-family situation: the observed colour vetoes app_state.
  if (!colourFamilyVetoesVariant(pageColourFamily, appStateVariant)) return null;
  const adjacents = COLOUR_FAMILY_TINT_BOUNDARY[pageColourFamily];
  if (!adjacents) return null;
  for (const adj of adjacents) {
    if (soleVariantForColourFamily(adj) === appStateVariant) return appStateVariant;
  }
  return null;
}

// ── Variant classifier ───────────────────────────────────────────────────

/**
 * Map of variant -> tokens that should match the bottom selector text.
 * Order is "high specificity first" so we don't accidentally match
 * "POKÉMON" inside a sentence that already had a more specific variant
 * keyword (e.g. an OCR run that returns "POKEMON SHINY LUCKY" gets the
 * highest-specificity match).
 */
// Non-letter boundary — matches at string start / end or at any non-letter
// character. Unlike \b, digits do NOT count as word chars for anchoring, so
// concatenated Vision reads like "SHINY01420143" or "3LUCKY0144" still
// match the label token while genuine word extensions ("POKEMONSTAY",
// "LUCKYCHARM") correctly do not. See the 2026-07-04 iPhone 12 trial log for
// runtime evidence: Vision returns label+adjacent-dex-number strings ~5x more
// often than clean labels on the pill crop, and the previous \b-anchored
// regexes zero-matched every concatenated form.
const L = "(?:^|[^A-Za-z])"; // leading non-letter boundary
const R = "(?:[^A-Za-z]|$)"; // trailing non-letter boundary
const LD = "(?:^|[^A-Za-z0-9])"; // stricter: also excludes digits (for 100%/3-star)
const RD = "(?:[^A-Za-z0-9]|$)";

export const VARIANT_TOKENS: Array<[PokedexVariant, RegExp[]]> = [
  // High-specificity variants first.
  // 2026-08-30 (inflated-Perfect heal): the "three star" / "3 star" regexes
  // were dropped. The real ★100% pill never renders that text, so those tokens
  // could only ever match stray OCR — and perfect sits first in this
  // most-specific-wins table, so one stray match flipped the whole frame's
  // commits into the Perfect dex.
  ["perfect",  [new RegExp(`${LD}100\\s*%${RD}`, "i"), new RegExp(`${L}perfect${R}`, "i")]],
  ["gmax",     [new RegExp(`${L}g[-\\s]?max${R}`, "i"), new RegExp(`${L}gigantamax${R}`, "i")]],
  ["mega",     [new RegExp(`${L}mega${R}`, "i")]],
  ["shadow",   [new RegExp(`${L}shadow${R}`, "i")]],
  ["purified", [new RegExp(`${L}purified${R}`, "i")]],
  ["lucky",    [new RegExp(`${L}lucky${R}`, "i")]],
  ["shiny",    [new RegExp(`${L}shiny${R}`, "i")]],
  ["xxs",      [new RegExp(`${L}xxs${R}`, "i")]],
  ["xxl",      [new RegExp(`${L}xxl${R}`, "i")]],
  // Catch-all main tab is last so the more specific keywords win when an
  // OCR pass returns multiple variant words on the same crop (the center
  // pill text plus the partial neighbours).
  //
  // The `ok[eé]mon` alternate is a Vision-quirk fallback: on iPhone 12 the
  // stylised P in the Pokémon logo is truncated by VNRecognizeText ~50% of
  // the time on the pill crop, producing "OKÉMON" / "OKÉMON0141" etc.
  // Confirmed against real device log 2026-07-04 (184 of 618 Vision reads).
  ["pokemon",  [new RegExp(`${L}pok[eé]mon${R}`, "i"), new RegExp(`${L}pokemon${R}`, "i"), new RegExp(`${L}ok[eé]mon${R}`, "i")]],
];

/**
 * Colour-narrowed token match (colour-narrowed pill re-read, 2026-06-19).
 *
 * Match `text` against ONLY the variant tokens whose variant is allowed for the
 * page-colour `family` (per [[COLOUR_FAMILY_ALLOWED_VARIANTS]]). Because the
 * candidate lexicon is restricted to the family's allowed set BEFORE matching,
 * the colour-veto invariant holds automatically: the returned variant is always
 * a member of `COLOUR_FAMILY_ALLOWED_VARIANTS[family]`, so an OCR run that
 * happens to read an impossible keyword (e.g. "MEGA" on a cyan page) can never
 * resolve. Returns the highest-specificity allowed match, or null.
 *
 * No hardcoded variant literals: the allowed set is read from the veto map and
 * the regexes from [[VARIANT_TOKENS]]; this function only intersects them.
 */
export function matchVariantTokenForColourFamily(
  text: string,
  family: string | null | undefined,
): PokedexVariant | null {
  if (!text) return null;
  // Family is optional: when it's known, we use it PURELY as a veto against
  // impossible matches (colour-narrowed pill re-read invariant preserved).
  // When family is null/unknown (which happens frequently in the Vision-only
  // trial because deriveVetoOnlyFamily is intentionally strict), fall through
  // and try every variant — the pill text alone is authoritative.
  //
  // Prior behaviour (bailing on null family) meant Vision reads at 100%
  // confidence like "POKÉMON" / "SHINY" / "LUCKY" produced matched:null on
  // every frame, verified against 2026-07-04 iPhone 12 device log.
  const allowed = isDexPageColourFamily(family)
    ? COLOUR_FAMILY_ALLOWED_VARIANTS[family]
    : null;
  for (const [variant, regexes] of VARIANT_TOKENS) {
    if (allowed && !allowed.includes(variant)) continue;
    for (const re of regexes) {
      if (re.test(text)) return variant;
    }
  }
  return null;
}

/**
 * Pure hue → dex page-colour family classifier (colour-narrowed pill re-read,
 * 2026-06-19). Derived EXACTLY from the documented hue ranges in the
 * [[COLOUR_FAMILY_ALLOWED_VARIANTS]] doc block above (no new literals):
 *   - dex_cyan          183-212
 *   - dex_golden         38-62
 *   - dex_peach           5-28
 *   - dex_pink          322-360 ∪ 0-8   (wraps zero)
 *   - dex_mint          145-190
 *   - dex_shadow_purple 225-265
 *
 * Used only when the caller has a peak hue but no pre-classified family (e.g.
 * the adapter, which receives the family directly from the hook's
 * classifyPaletteRobust tracker, prefers that and never needs this). Returns
 * null for a hue outside every documented band, or a non-finite input.
 *
 * The dex_mint (145-190) and dex_cyan (183-212) bands overlap at 183-190; mint
 * is tested first so the narrower teal band wins there, matching the veto-map
 * doc order. This is corroboration-only context — colour never positively
 * SELECTS a variant on its own except for single-variant families via
 * soleVariantForColourFamily.
 */
export function colourFamilyFromHue(hue: number | null | undefined): DexPageColourFamily | null {
  if (hue == null || !Number.isFinite(hue)) return null;
  // Normalise into [0, 360).
  const h = ((hue % 360) + 360) % 360;
  const inBand = (lo: number, hi: number) => h >= lo && h <= hi;
  if (inBand(5, 28)) return "dex_peach";
  if (inBand(38, 62)) return "dex_golden";
  if (inBand(145, 190)) return "dex_mint";
  if (inBand(183, 212)) return "dex_cyan";
  if (inBand(225, 265)) return "dex_shadow_purple";
  // dex_pink wraps zero: 322-360 ∪ 0-8.
  if (inBand(322, 360) || inBand(0, 8)) return "dex_pink";
  return null;
}

// (2026-08-30 dead-code removal) `classifyVariantFromSelectorText` and
// `classifyVariantWithSupportingSignals` were DELETED: no production caller
// remained (the live path routes variants through activePillClassifier +
// variantResolution), and their "most specific token wins anywhere in the
// string" behaviour meant any stray "100%" in header/selector OCR classified a
// frame as the Perfect dex. VARIANT_TOKENS itself stays: it backs
// matchVariantTokenForColourFamily, which is family-veto-filtered.

// ── Region helpers ───────────────────────────────────────────────────────

const REGION_TOKENS: Array<[PokedexRegionLite, RegExp]> = [
  ["kanto",  /\bkanto\b/i],
  ["johto",  /\bjohto\b/i],
  ["hoenn",  /\bhoenn\b/i],
  ["sinnoh", /\bsinnoh\b/i],
  ["unova",  /\bunova\b/i],
  ["kalos",  /\bkalos\b/i],
  ["alola",  /\balola\b/i],
  ["galar",  /\bgalar\b/i],
  ["hisui",  /\bhisui\b/i],
  ["paldea", /\bpaldea\b/i],
  ["unidentified", /\bunidentified\b/i],
];

export function classifyRegionFromHeader(text: string): PokedexRegionLite {
  if (!text) return "unknown";
  for (const [region, re] of REGION_TOKENS) {
    if (re.test(text)) return region;
  }
  return "unknown";
}

/**
 * Parse a header count card. Accepts "138", "151/151", "72/151".
 */
export function parseHeaderCount(text: string): { count: number | null; total: number | null } {
  if (!text) return { count: null, total: null };
  const ratio = text.match(/(\d{1,4})\s*\/\s*(\d{1,4})/);
  if (ratio) {
    return { count: parseInt(ratio[1], 10), total: parseInt(ratio[2], 10) };
  }
  const single = text.match(/\b(\d{1,4})\b/);
  if (single) {
    return { count: parseInt(single[1], 10), total: null };
  }
  return { count: null, total: null };
}

// ── Cell sequence helpers (sparse-tab aware) ─────────────────────────────

/**
 * Build a VariantFrame from raw per-cell OCR observations.
 *
 * IMPORTANT contract:
 *   - We DO NOT fabricate missing entries for gaps in the dex-number
 *     sequence. The caller is responsible for treating "no observation"
 *     as "no information" — never "missing".
 *   - For sparse variants (shadow/purified/mega/gmax) the absence of an
 *     entry is normal; this helper additionally tags the frame's first
 *     observed number so downstream can show "starts at #018" hints
 *     without inferring missing earlier numbers.
 */
export interface BuildFrameInput {
  variant: PokedexVariant;
  variantConfidence: number;
  region: PokedexRegionLite;
  headerCount: number | null;
  headerTotal: number | null;
  cells: VariantCellObservation[];
  overlayFenced?: boolean;
}

export function buildVariantFrame(input: BuildFrameInput): VariantFrame {
  // Deduplicate by dex number, keeping the highest-priority state.
  // Priority: present > unknown > missing — a single "present" sighting
  // anywhere in the frame wins over a sibling crop that thought the cell
  // was empty (the visible sprite is the ground truth).
  const STATE_RANK: Record<CellState, number> = { present: 3, unknown: 2, missing: 1 };
  const byNumber = new Map<number, VariantCellObservation>();
  for (const cell of input.cells) {
    if (!Number.isFinite(cell.dexNumber) || cell.dexNumber < 1) continue;
    const prev = byNumber.get(cell.dexNumber);
    if (!prev || STATE_RANK[cell.state] > STATE_RANK[prev.state]) {
      byNumber.set(cell.dexNumber, cell);
    }
  }
  const cells = Array.from(byNumber.values()).sort((a, b) => a.dexNumber - b.dexNumber);
  return {
    variant: input.variant,
    region: input.region,
    headerCount: input.headerCount,
    headerTotal: input.headerTotal,
    cells,
    variantConfidence: input.variantConfidence,
    overlayFenced: !!input.overlayFenced,
  };
}

/**
 * Merge multiple frames captured across scroll positions on the SAME
 * variant into a single deduped frame. Cells from later frames upgrade
 * earlier observations using the same STATE_RANK ordering as
 * buildVariantFrame.
 *
 * If frames disagree on `variant`, the highest-confidence frame wins and
 * the others are dropped — never silently merge across variants.
 */
export function mergeVariantFrames(frames: VariantFrame[]): VariantFrame | null {
  if (frames.length === 0) return null;
  const byVariant = new Map<PokedexVariant, VariantFrame[]>();
  for (const f of frames) {
    const list = byVariant.get(f.variant) ?? [];
    list.push(f);
    byVariant.set(f.variant, list);
  }
  // Pick the variant with the highest summed confidence.
  let bestVariant: PokedexVariant = frames[0].variant;
  let bestScore = -Infinity;
  byVariant.forEach((list, variant) => {
    const score = list.reduce((s, f) => s + f.variantConfidence, 0);
    if (score > bestScore) {
      bestScore = score;
      bestVariant = variant;
    }
  });
  const accepted = byVariant.get(bestVariant) ?? [];
  return buildVariantFrame({
    variant: bestVariant,
    variantConfidence: Math.max(...accepted.map((f) => f.variantConfidence)),
    region: accepted.find((f) => f.region !== "unknown")?.region ?? "unknown",
    headerCount: accepted.map((f) => f.headerCount).find((n) => n != null) ?? null,
    headerTotal: accepted.map((f) => f.headerTotal).find((n) => n != null) ?? null,
    cells: accepted.flatMap((f) => f.cells),
    overlayFenced: accepted.some((f) => f.overlayFenced),
  });
}

/**
 * Test guard: a Pokédex variant frame must NEVER carry inventory shape.
 * Mirrors `isPokedexPayloadSafe` from pokedexOcr.ts but tightened to the
 * new shape (cells are { dexNumber, state, yPct? } only).
 */
export function isVariantFramePayloadSafe(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  if (!Array.isArray(p.cells)) return false;
  const inventoryFields = [
    "cp", "ivPercent", "instanceId", "speciesId", "flags",
    "nickname", "captureMethod", "tradePartner", "iv",
  ];
  for (const f of inventoryFields) {
    if (f in p && p[f] !== null && p[f] !== undefined) return false;
  }
  for (const cell of p.cells as unknown[]) {
    if (!cell || typeof cell !== "object") return false;
    const c = cell as Record<string, unknown>;
    for (const f of inventoryFields) {
      if (f in c && c[f] !== null && c[f] !== undefined) return false;
    }
  }
  return true;
}

// ── Sparse tab predicates ────────────────────────────────────────────────

export function isSparseVariant(v: PokedexVariant): boolean {
  return SPARSE_VARIANTS.includes(v);
}

/**
 * For a sparse variant, given the set of visible dex numbers, return the
 * smallest observed number ("starts at #018" hint). Returns null when no
 * cells were observed.
 */
export function sparseStartNumber(frame: VariantFrame): number | null {
  if (!isSparseVariant(frame.variant)) return null;
  if (frame.cells.length === 0) return null;
  return frame.cells[0].dexNumber;
}

// ── Overlay fencing ──────────────────────────────────────────────────────

/**
 * Decide whether a cell at the given y% should be treated as authoritative
 * for "missing" or only as a passive observation. The PoGo Pokédex UI
 * places a floating Regions button bottom-right, a search/X chip at the
 * top of the carousel, and the carousel itself in the bottom ~10-14%.
 *
 * Cells at yPct >= 0.86 may be partially occluded; we still ingest
 * present cells from this band (a sprite that is visible IS visible) but
 * we never use those cells to assert "missing".
 */
export function isCellInOverlayBand(yPct: number | undefined): boolean {
  if (yPct == null || !Number.isFinite(yPct)) return false;
  return yPct >= 0.86;
}

/**
 * Filter a cell list for safe "missing" assertion (used when persisting
 * cells whose state is "missing"). Present cells are always kept.
 */
export function cellsSafeForMissingAssertion(
  cells: VariantCellObservation[],
): VariantCellObservation[] {
  return cells.filter((c) => c.state === "present" || !isCellInOverlayBand(c.yPct));
}

// ── Veto-only page-colour sample (vision-only trial) ─────────────────────────

/**
 * Body-background sample zones for [[deriveVetoOnlyFamily]], in fractional
 * (x, y) of the frame. Mirrors the veto-relevant slice of the deleted
 * dexPreOcrGate's PRE_OCR_GATE_ZONES: the far-left (~2%) and far-right (~97%)
 * columns at three vertical offsets (30 / 50 / 65%), where a real Pokédex page
 * shows only the tinted body backdrop (never a tile, the header pill, or the
 * filter-pill row). Six zones total.
 */
export const VETO_COLOUR_SAMPLE_ZONES: ReadonlyArray<{ x: number; y: number }> = [
  { x: 0.02, y: 0.3 },
  { x: 0.02, y: 0.5 },
  { x: 0.02, y: 0.65 },
  { x: 0.97, y: 0.3 },
  { x: 0.97, y: 0.5 },
  { x: 0.97, y: 0.65 },
];

/** Edge length (source px) of the square patch averaged per veto sample zone. */
export const VETO_COLOUR_PATCH_SIZE = 8;

/**
 * Average an 8-px-square patch of an RGBA buffer, clamped around the fractional
 * coordinate (xPct, yPct). Pure and total; returns black for a degenerate frame
 * rather than throwing.
 */
function sampleVetoZoneRgb(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  xPct: number,
  yPct: number,
  patchSize = VETO_COLOUR_PATCH_SIZE,
): { r: number; g: number; b: number } {
  if (width < 1 || height < 1) return { r: 0, g: 0, b: 0 };
  const half = Math.floor(patchSize / 2);
  const cx = Math.round(xPct * (width - 1));
  const cy = Math.round(yPct * (height - 1));
  const x0 = Math.max(0, cx - half);
  const x1 = Math.min(width - 1, cx + half);
  const y0 = Math.max(0, cy - half);
  const y1 = Math.min(height - 1, cy + half);
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const i = (y * width + x) * 4;
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      n += 1;
    }
  }
  if (n === 0) return { r: 0, g: 0, b: 0 };
  return { r: r / n, g: g / n, b: b / n };
}

/** Per-channel median of a set of RGB samples. Robust to a minority of outlier
 * zones (a sprite/ribbon/glare patch contaminating one or two zones). Pure. */
function medianVetoRgb(
  samples: ReadonlyArray<{ r: number; g: number; b: number }>,
): { r: number; g: number; b: number } {
  if (samples.length === 0) return { r: 0, g: 0, b: 0 };
  const median = (vals: number[]): number => {
    const s = [...vals].sort((a, b) => a - b);
    const mid = s.length >> 1;
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
  };
  return {
    r: median(samples.map((s) => s.r)),
    g: median(samples.map((s) => s.g)),
    b: median(samples.map((s) => s.b)),
  };
}

/**
 * Classify a median-backdrop RGB into a dex page-colour family, or null.
 *
 * Vision-only trial: this is the INLINED veto-only slice of the deleted
 * dexPreOcrGate.classifyPalette. Predicates are copied verbatim from that file
 * (do NOT import — it is gone). Order matters: first match wins.
 *   - menu_green is matched first and returns null (it is the Pokémon GO main
 *     menu, never a dex page — so it must never carry a veto family).
 *   - A backdrop matching no dex family returns null so callers cleanly skip the
 *     veto entirely (an "unknown" wash never rejects a variant).
 * Pure and total.
 */
function classifyVetoPalette(
  r: number,
  g: number,
  b: number,
): DexPageColourFamily | null {
  // Pokémon GO main menu green — NOT a dex page. Return null (no veto).
  if (g >= 235 && g - r >= 25 && g - b >= 25) return null;
  // dex_cyan: default, XXL, XXS, region selector (blue-dominant).
  if (b >= 215 && b - r >= 15 && (g - r >= 10 || (b - r >= 60 && g >= 170)) && b >= g - 2) {
    return "dex_cyan";
  }
  // dex_golden: Shiny, Lucky (warm yellow, R high, B well below).
  if (r >= 220 && r - b >= 50 && g - b >= 30 && g >= 200 && r - g <= 40) {
    return "dex_golden";
  }
  // dex_peach: 100% / Perfect (salmon, R high, G approx B). Stricter subset of
  // pink, so it MUST run before pink.
  if (r >= 220 && r - g >= 40 && r - b >= 50 && Math.abs(g - b) <= 25) {
    return "dex_peach";
  }
  // dex_pink: G-Max, Mega (rose, R high, G below R, B between).
  if (r >= 210 && r - g >= 20 && r - b >= 20 && g < 200) {
    return "dex_pink";
  }
  // dex_mint: Purified (teal, G slightly above R and B).
  if (g >= 195 && g - r >= 20 && Math.abs(g - b) <= 30 && b >= 180 && b - r >= 5) {
    return "dex_mint";
  }
  // dex_shadow_purple: Shadow (lavender, B highest, R approx G).
  if (b >= 200 && b - r >= 20 && b - g >= 30 && r >= 160 && g >= 150 && Math.abs(r - g) <= 20) {
    return "dex_shadow_purple";
  }
  return null;
}

/**
 * Derive the VETO-ONLY page-colour family for a frame from its full-frame RGBA.
 *
 * Vision-only trial: this replaces the deleted pre-OCR palette gate as the
 * source of the colour veto. It samples six body-background zones (the far-left
 * and far-right columns at 30/50/65% height, 8-px-square each), takes the
 * per-channel median across all zones to shrug off a sprite/glare-contaminated
 * zone, and classifies the median via the inlined [[classifyVetoPalette]]
 * predicates (copied from the deleted dexPreOcrGate.classifyPalette).
 *
 * Returns the matched family, or null when the backdrop matches no known dex
 * family (or is the Pokémon GO menu green). Null is the clean "skip the veto"
 * signal — colour is corroboration-only and never positively selects a variant.
 * Pure and total.
 */
export function deriveVetoOnlyFamily(imageData: ImageData): DexPageColourFamily | null {
  const { data, width, height } = imageData;
  if (width < 1 || height < 1) return null;
  const zones = VETO_COLOUR_SAMPLE_ZONES.map((z) =>
    sampleVetoZoneRgb(data, width, height, z.x, z.y),
  );
  const { r, g, b } = medianVetoRgb(zones);
  return classifyVetoPalette(r, g, b);
}
